/**
 * Organization Manager
 * 
 * Provides focused organization management with consistent patterns
 */

import type { Organization, Address as OrgAddressType, OrganizationStatusType, ResourceUsage, Space, Club, User } from '@/lib/types';
import { generateNumericId } from '@/lib/utils';
import { format } from 'date-fns';
import { isSampleDataMode } from '@/lib/config/env';
import { FilterBuilder } from '@/lib/utils/apiHelpers';
import { validateEmail, validateName, validateStatus } from '@/lib/validation';
// Sample data is now imported dynamically to avoid build issues in production

// Remove direct imports to avoid circular dependencies
// Import managers dynamically when needed

interface CreateOrganizationData extends Omit<Organization, 'id' | 'dateCreated' | 'dateUpdated'> {
  // Additional organization creation fields can be added here
  setupComplete?: boolean;
}

interface OrganizationFilters {
  status?: OrganizationStatusType;
  platformPlanId?: string;
  search?: string;
}

/**
 * Organization manager that unifies organization operations
 */
// Global data storage for organization manager - singleton pattern
const globalOrganizationData: {
  currentOrganization: Organization;
  organizations: Organization[];
  isInitialized: boolean;
  initializationPromise: Promise<void> | null;
} = {
  currentOrganization: {} as Organization,
  organizations: [],
  isInitialized: false,
  initializationPromise: null
};

export class OrganizationManager {
  private readonly DELAY_MS = 100;

  constructor() {
    // Only initialize once globally
    if (!globalOrganizationData.initializationPromise) {
      globalOrganizationData.initializationPromise = this.initializeData();
    }
  }

  private async initializeData(): Promise<void> {
    try {
      console.log('🔧 [OrganizationManager] Initializing organization data...');
      
      // Initialize with empty defaults first
      globalOrganizationData.currentOrganization = {} as Organization;
      globalOrganizationData.organizations = [];

      // Only attempt to load sample data if we're in sample data mode
      if (isSampleDataMode()) {
        console.log('📊 [OrganizationManager] Loading sample data...');
        try {
          // Import sample data directly from the organizations file to avoid circular dependencies
          const { sampleOrganizationDataForAccountPage, sampleOrganisationsDataForSuperAdmin } = await import('@/lib/sample-data/organizations');
          
          console.log('📋 [OrganizationManager] Sample data loaded:', {
            currentOrg: sampleOrganizationDataForAccountPage ? 'Found' : 'Not found',
            organizationsCount: Array.isArray(sampleOrganisationsDataForSuperAdmin) ? sampleOrganisationsDataForSuperAdmin.length : 0
          });
          
          // Only use sample data if it's actually defined and valid
          if (sampleOrganizationDataForAccountPage && typeof sampleOrganizationDataForAccountPage === 'object') {
            globalOrganizationData.currentOrganization = JSON.parse(JSON.stringify(sampleOrganizationDataForAccountPage));
            console.log('✅ [OrganizationManager] Current organization data found');
          }
          
          if (sampleOrganisationsDataForSuperAdmin && Array.isArray(sampleOrganisationsDataForSuperAdmin) && sampleOrganisationsDataForSuperAdmin.length > 0) {
            globalOrganizationData.organizations = JSON.parse(JSON.stringify(sampleOrganisationsDataForSuperAdmin));
            console.log('✅ [OrganizationManager] Organizations loaded:', sampleOrganisationsDataForSuperAdmin.length);
          } else {
            console.warn('⚠️ [OrganizationManager] No organizations data found in sample data');
          }
        } catch (importError) {
          console.error('❌ [OrganizationManager] Sample data import failed:', importError);
          console.error('❌ [OrganizationManager] Import error details:', importError);
          // Keep empty defaults
        }
      } else {
        console.log('🏭 [OrganizationManager] Production mode - no sample data');
      }
      
      globalOrganizationData.isInitialized = true;
      console.log('✅ [OrganizationManager] Initialization complete');
    } catch (error) {
      console.error('❌ [OrganizationManager] Failed to initialize organization data:', error);
      // Ensure we always have valid defaults
      globalOrganizationData.currentOrganization = {} as Organization;
      globalOrganizationData.organizations = [];
      globalOrganizationData.isInitialized = true;
    }
  }

  private async ensureInitialized(): Promise<void> {
    if (!globalOrganizationData.isInitialized && globalOrganizationData.initializationPromise) {
      await globalOrganizationData.initializationPromise;
    }
  }

  /**
   * Get all organizations with unified logic
   */
  async getAllOrganizations(): Promise<Organization[]> {
    await this.ensureInitialized();
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        // Return cached data if available, otherwise load fresh
        if (globalOrganizationData.organizations.length > 0) {
          console.log('📋 [OrganizationManager] Returning cached organizations:', globalOrganizationData.organizations.length);
          return JSON.parse(JSON.stringify(globalOrganizationData.organizations));
        }
        
        try {
          const { sampleOrganisationsDataForSuperAdmin } = await import('@/lib/sample-data/organizations');
          if (sampleOrganisationsDataForSuperAdmin && Array.isArray(sampleOrganisationsDataForSuperAdmin)) {
            console.log('📋 [OrganizationManager] Returning organizations:', sampleOrganisationsDataForSuperAdmin.length);
            return JSON.parse(JSON.stringify(sampleOrganisationsDataForSuperAdmin));
          }
        } catch (error) {
          console.error('Failed to load organizations sample data:', error);
        }
        return [];
      }

      // In production, would integrate with actual organization service
      console.log('OrganizationManager: Production mode - would fetch from Firebase');
      return [];
    });
  }

  /**
   * Get organizations with flexible filtering
   */
  async getOrganizations(filters: OrganizationFilters = {}): Promise<Organization[]> {
    const allOrganizations = await this.getAllOrganizations();
    
    return new FilterBuilder(allOrganizations)
      .where('status', filters.status)
      .where('platformPlanId', filters.platformPlanId)
      .search(filters.search, ['name', 'description'])
      .build();
  }

  /**
   * Get organization by ID
   */
  async getOrganizationById(id: string): Promise<Organization | undefined> {
    await this.ensureInitialized();
    return this.withDelay(async () => {
      const allOrganizations = await this.getAllOrganizations();
      // Looking for organization by ID
      const found = allOrganizations.find(org => org.id === id);
      console.log('📋 [OrganizationManager] Found organization:', found ? found.name : 'NOT FOUND');
      return found;
    });
  }

  /**
   * Get the current user's organization
   */
  async getCurrentOrganization(): Promise<Organization> {
    await this.ensureInitialized();
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        // Return cached data if available, otherwise load fresh
        if (globalOrganizationData.currentOrganization && Object.keys(globalOrganizationData.currentOrganization).length > 0) {
          return JSON.parse(JSON.stringify(globalOrganizationData.currentOrganization));
        }
        
        try {
          const { sampleOrganizationDataForAccountPage } = await import('@/lib/sample-data/organizations');
          if (sampleOrganizationDataForAccountPage) {
            return JSON.parse(JSON.stringify(sampleOrganizationDataForAccountPage));
          }
        } catch (error) {
          console.error('Failed to load current organization sample data:', error);
        }
        return {} as Organization;
      }

      // In production, would get from user context
      console.log('OrganizationManager: Production mode - would get from user context');
      return {} as Organization;
    });
  }

  /**
   * Create new organization
   */
  async createOrganization(organizationData: CreateOrganizationData): Promise<Organization> {
    return this.withDelay(async () => {
      const now = format(new Date(), 'yyyy-MM-dd HH:mm:ss');
      const newOrganization: Organization = {
        id: generateNumericId(10),
        ...organizationData,
        dateCreated: now,
        dateUpdated: now,
      };

      if (isSampleDataMode()) {
        globalOrganizationData.organizations.push(newOrganization);
        return JSON.parse(JSON.stringify(newOrganization));
      }

      // In production, would create through Firebase
      console.log('OrganizationManager: Production mode - would create in Firebase');
      return newOrganization;
    });
  }

  /**
   * Update existing organization
   */
  async updateOrganization(id: string, updates: Partial<Organization>): Promise<Organization | null> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        const orgIndex = globalOrganizationData.organizations.findIndex((o: Organization) => o.id === id);
        if (orgIndex !== -1) {
          // Handle address updates properly
          if (updates.address) {
            globalOrganizationData.organizations[orgIndex].address = {
              ...(globalOrganizationData.organizations[orgIndex].address || {}),
              ...updates.address
            } as OrgAddressType;
            delete updates.address;
          }

          // Handle platform plan updates
          if (updates.platformPlanId !== undefined) {
            globalOrganizationData.organizations[orgIndex].platformPlanId = updates.platformPlanId;
          }
          if (updates.platformPlanSubscriptionStartDate !== undefined) {
            globalOrganizationData.organizations[orgIndex].platformPlanSubscriptionStartDate = updates.platformPlanSubscriptionStartDate;
          }

          globalOrganizationData.organizations[orgIndex] = {
            ...globalOrganizationData.organizations[orgIndex],
            ...updates,
            dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
          };

          // Update current organization if it's the same
          if (globalOrganizationData.currentOrganization.id === id) {
            globalOrganizationData.currentOrganization = JSON.parse(JSON.stringify(globalOrganizationData.organizations[orgIndex]));
          }

          return JSON.parse(JSON.stringify(globalOrganizationData.organizations[orgIndex]));
        }
        return null;
      }

      // In production, would update through Firebase
      console.log('OrganizationManager: Production mode - would update in Firebase');
      const existingOrg = await this.getOrganizationById(id);
      if (!existingOrg) return null;
      
      return {
        ...existingOrg,
        ...updates,
        dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
      };
    });
  }

  /**
   * Update current organization
   */
  async updateCurrentOrganization(updates: Partial<Organization>): Promise<Organization | null> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        // Handle address updates properly
        if (updates.address) {
          globalOrganizationData.currentOrganization.address = {
            ...(globalOrganizationData.currentOrganization.address || {}),
            ...updates.address
          } as OrgAddressType;
          delete updates.address;
        }

        // Handle platform plan updates
        if (updates.platformPlanId !== undefined) {
          globalOrganizationData.currentOrganization.platformPlanId = updates.platformPlanId;
        }
        if (updates.platformPlanSubscriptionStartDate !== undefined) {
          globalOrganizationData.currentOrganization.platformPlanSubscriptionStartDate = updates.platformPlanSubscriptionStartDate;
        }

        globalOrganizationData.currentOrganization = {
          ...globalOrganizationData.currentOrganization,
          ...updates,
          dateUpdated: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        };

        // Also update in the organizations list if it exists there
        const orgIndex = globalOrganizationData.organizations.findIndex((o: Organization) => o.id === globalOrganizationData.currentOrganization.id);
        if (orgIndex !== -1) {
          globalOrganizationData.organizations[orgIndex] = JSON.parse(JSON.stringify(globalOrganizationData.currentOrganization));
        }

        return JSON.parse(JSON.stringify(globalOrganizationData.currentOrganization));
      }

      // In production, would update through Firebase
      console.log('OrganizationManager: Production mode - would update current organization in Firebase');
      return globalOrganizationData.currentOrganization;
    });
  }

  /**
   * Delete organization
   */
  async deleteOrganization(id: string): Promise<boolean> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        const initialLength = globalOrganizationData.organizations.length;
        globalOrganizationData.organizations = globalOrganizationData.organizations.filter((o: Organization) => o.id !== id);
        return globalOrganizationData.organizations.length < initialLength;
      }

      // In production, would delete through Firebase
      console.log('OrganizationManager: Production mode - would delete from Firebase');
      return true;
    });
  }

  /**
   * Get organizations by status
   */
  async getOrganizationsByStatus(status: OrganizationStatusType): Promise<Organization[]> {
    return this.getOrganizations({ status });
  }

  /**
   * Get active organizations
   */
  async getActiveOrganizations(): Promise<Organization[]> {
    return this.getOrganizationsByStatus('Active');
  }

  /**
   * Get organizations by platform plan
   */
  async getOrganizationsByPlan(platformPlanId: string): Promise<Organization[]> {
    return this.getOrganizations({ platformPlanId });
  }

  /**
   * Search organizations
   */
  async searchOrganizations(searchTerm: string): Promise<Organization[]> {
    return this.getOrganizations({ search: searchTerm });
  }

  /**
   * Get organization statistics
   */
  async getOrganizationStats(): Promise<{
    total: number;
    active: number;
    archived: number;
    suspended: number;
    byStatus: Record<string, number>;
    byPlan: Record<string, number>;
  }> {
    return this.withDelay(async () => {
      const organizations = await this.getAllOrganizations();
      
      const byStatus = organizations.reduce((acc, org) => {
        const status = org.status || 'Unknown';
        acc[status] = (acc[status] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      const byPlan = organizations.reduce((acc, org) => {
        const plan = org.platformPlanId || 'None';
        acc[plan] = (acc[plan] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);

      return {
        total: organizations.length,
        active: byStatus.Active || 0,
        archived: byStatus.Archived || 0,
        suspended: byStatus.Suspended || 0,
        byStatus,
        byPlan,
      };
    });
  }

  /**
   * Calculate current resource usage for an organization
   */
  async calculateResourceUsage(organizationId: string): Promise<ResourceUsage> {
    return this.withDelay(async () => {
      if (isSampleDataMode()) {
        // Use dynamic imports to avoid circular dependencies
        const { spaceManager } = await import('./spaceManager');
        const { clubManager } = await import('./clubManager');
        const { userManager } = await import('./userManager');

        const spaces = await spaceManager.getAllSpaces();
        const clubs = await clubManager.getAllClubs({ organizationId });
        const users = await userManager.getAllUsers();

        const activeSpaces = spaces.filter((space: Space) => 
          space.organizationId === organizationId && space.status === 'active'
        ).length;
        const totalClubs = clubs.filter((club: Club) => club.organizationId === organizationId).length;

        // Count users by organization
        let totalUsers = 0;
        const usersPerClub: Record<string, number> = {};

        users.forEach((user: User) => {
          if (user.organizationId === organizationId) {
            totalUsers++;
            
            // Count users per club
            clubs.forEach((club: Club) => {
              if (club.organizationId === organizationId && 
                  user.associatedClubIds?.includes(club.id)) {
                usersPerClub[club.id] = (usersPerClub[club.id] || 0) + 1;
              }
            });
          }
        });

        // Count unique sports across all clubs
        const sportsOfferedSet = new Set<string>();
        clubs.forEach((club: Club) => {
          if (club.organizationId === organizationId && club.sportsOffered?.length) {
            club.sportsOffered.forEach((sport: string) => {
              sportsOfferedSet.add(sport);
            });
          }
        });

        return {
          activeSpaces,
          totalClubs,
          totalUsers,
          sportsOffered: Array.from(sportsOfferedSet),
          usersPerClub,
        };
      }

      // In production, would calculate resource usage through Firebase
      console.log('OrganizationManager: Production mode - would calculate resource usage in Firebase');
      return {
        activeSpaces: 0,
        totalClubs: 0,
        totalUsers: 0,
        sportsOffered: [],
        usersPerClub: {},
      };
    });
  }

  /**
   * Calculate resource usage for multiple organizations
   */
  async calculateBulkResourceUsage(organizationIds: string[]): Promise<Record<string, ResourceUsage>> {
    const results: Record<string, ResourceUsage> = {};
    
    // Calculate usage for each organization efficiently
    for (const orgId of organizationIds) {
      results[orgId] = await this.calculateResourceUsage(orgId);
    }
    
    return results;
  }

  /**
   * Get the maximum users per club for an organization
   */
  async getMaxUsersPerClub(organizationId: string): Promise<number> {
    const usage = await this.calculateResourceUsage(organizationId);
    const userCounts = Object.values(usage.usersPerClub);
    
    return userCounts.length > 0 ? Math.max(...userCounts) : 0;
  }

  /**
   * Check if adding a resource would exceed current capacity (helper for compliance checking)
   */
  async getResourceImpact(
    organizationId: string, 
    resourceType: 'space' | 'club' | 'user',
    targetClubId?: string
  ): Promise<{ currentCount: number; newCount: number }> {
    const usage = await this.calculateResourceUsage(organizationId);
    
    let currentCount: number;
    let newCount: number;
    
    switch (resourceType) {
      case 'space':
        currentCount = usage.activeSpaces;
        newCount = currentCount + 1;
        break;
      case 'club':
        currentCount = usage.totalClubs;
        newCount = currentCount + 1;
        break;
      case 'user':
        if (targetClubId) {
          currentCount = usage.usersPerClub[targetClubId] || 0;
          newCount = currentCount + 1;
        } else {
          currentCount = usage.totalUsers;
          newCount = currentCount + 1;
        }
        break;
      default:
        currentCount = 0;
        newCount = 0;
    }
    
    return { currentCount, newCount };
  }

  /**
   * Get resource usage summary for dashboard display
   */
  async getResourceSummary(organizationId: string): Promise<{
    spaces: { count: number; label: string };
    clubs: { count: number; label: string };
    users: { count: number; label: string };
    sports: { count: number; label: string };
  }> {
    const usage = await this.calculateResourceUsage(organizationId);
    
    return {
      spaces: {
        count: usage.activeSpaces,
        label: usage.activeSpaces === 1 ? 'Space' : 'Spaces'
      },
      clubs: {
        count: usage.totalClubs,
        label: usage.totalClubs === 1 ? 'Club' : 'Clubs'
      },
      users: {
        count: usage.totalUsers,
        label: usage.totalUsers === 1 ? 'User' : 'Users'
      },
      sports: {
        count: usage.sportsOffered.length,
        label: usage.sportsOffered.length === 1 ? 'Sport' : 'Sports'
      }
    };
  }

  /**
   * Validate organization data using shared validation utilities
   */
  validateOrganizationData(data: Partial<Organization>): {
    isValid: boolean;
    errors: string[];
    warnings: string[];
  } {
    const errors: string[] = [];
    const warnings: string[] = [];

    // Validate name using shared utility
    const nameValidation = validateName(data.name || '', 'Organization name', 100);
    errors.push(...nameValidation.errors);

    // Validate status using shared utility  
    if (data.status) {
      const validStatuses: OrganizationStatusType[] = ['Active', 'Inactive'];
      const statusValidation = validateStatus(data.status, validStatuses, 'Organization');
      errors.push(...statusValidation.errors);
    } else {
      errors.push('Organization status is required');
    }

    // Validate email using shared utility
    if (data.email) {
      const emailValidation = validateEmail(data.email);
      if (!emailValidation.isValid && emailValidation.error) {
        errors.push(emailValidation.error);
      }
    }

    // Basic phone validation
    if (data.phone && data.phone.length > 0 && data.phone.length < 10) {
      warnings.push('Phone number seems too short');
    }

    return {
      isValid: errors.length === 0,
      errors,
      warnings,
    };
  }

  private async withDelay<T>(operation: () => Promise<T>): Promise<T> {
    await new Promise(resolve => setTimeout(resolve, this.DELAY_MS));
    return operation();
  }
}

// Export singleton instance
export const organizationManager = new OrganizationManager(); 
